// Generate a high-quality A4 portrait PDF from a header + table
// using jsPDF + jspdf-autotable. The output mirrors the on-screen
// score-sheet / results table exactly.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDMY } from "./date";

const A4_PORTRAIT_WIDTH_MM = 210;
const A4_PORTRAIT_HEIGHT_MM = 297;
const SIDE_MARGIN_MM = 10;
const TOP_MARGIN_MM = 10;

function drawHeader(doc, { competition, season, subtitle, clubName }) {
  const centerX = A4_PORTRAIT_WIDTH_MM / 2;
  let y = TOP_MARGIN_MM + 5;

  // Club name — centred, bold, at the top so there's no overprint with the
  // competition name. Skip the line entirely when no club name is set.
  if (clubName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(17);
    doc.text(clubName, centerX, y, { align: "center" });
    y += 6;
  }

  // Competition name — slightly smaller, sits below the club name.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(17);
  doc.text(competition.competition_name || "", centerX, y, { align: "center" });
  y += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(70);
  const metaParts = [
    competition.competition_date ? `Date: ${formatDMY(competition.competition_date)}` : null,
    competition.competition_type ? `Type: ${competition.competition_type}` : null,
    competition.category ? `Category: ${competition.category}` : null,
    competition.round_number ? `Round: ${competition.round_number}` : null,
    competition.judge_name ? `Judge: ${competition.judge_name}` : null,
    season ? `Season: ${season.season_name}` : null,
  ].filter(Boolean);
  doc.text(metaParts.join("   ·   "), centerX, y, { align: "center" });
  y += 5;

  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.text(subtitle.toUpperCase(), centerX, y, { align: "center" });
  y += 3;

  doc.setDrawColor(17);
  doc.setLineWidth(0.4);
  doc.line(SIDE_MARGIN_MM, y, A4_PORTRAIT_WIDTH_MM - SIDE_MARGIN_MM, y);
  return y + 3;
}

function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(140);
    const txt = `Camera Club Score · printed ${new Date().toLocaleString()} · page ${i}/${pageCount}`;
    doc.text(
      txt,
      A4_PORTRAIT_WIDTH_MM / 2,
      A4_PORTRAIT_HEIGHT_MM - 6,
      { align: "center" }
    );
  }
}

function safeFile(name, suffix) {
  return `${(name || "competition").replace(/[^a-z0-9-_]+/gi, "_")}_${suffix}.pdf`;
}

/**
 * Score sheet PDF — one row per photographer with empty Score and Place boxes.
 *
 * @param {object} args
 * @param {object} args.competition
 * @param {object|null} args.season
 * @param {Array<{photographer_code: string, photographer_name: string, image_title: string}>} args.rows
 */
export function savePdfA4Portrait_Scoresheet({ competition, season, rows, clubName }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const startY = drawHeader(doc, { competition, season, subtitle: "Judging Score Sheet", clubName });

  autoTable(doc, {
    startY,
    head: [["#", "I.D.", "Photographer", "Image Title", "Score", "Place"]],
    body: rows.map((r, i) => [
      String(i + 1),
      r.photographer_code || "",
      r.photographer_name || "",
      r.image_title || "",
      "", // empty for handwritten score
      "", // empty for handwritten place
    ]),
    margin: { left: SIDE_MARGIN_MM, right: SIDE_MARGIN_MM, top: 14, bottom: 12 },
    theme: "grid",
    headStyles: {
      fillColor: [243, 243, 243],
      textColor: [34, 34, 34],
      fontStyle: "bold",
      fontSize: 7.5,
      lineColor: [153, 153, 153],
      lineWidth: 0.2,
      halign: "left",
      cellPadding: 1.5,
      minCellHeight: 5.5,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 1.2,
      textColor: [17, 17, 17],
      lineColor: [153, 153, 153],
      lineWidth: 0.2,
      minCellHeight: 5.5,
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: {
      0: { halign: "center", cellWidth: 9, textColor: [102, 102, 102] },
      1: { fontStyle: "bold", cellWidth: 20 },
      2: { cellWidth: 40 },
      3: { cellWidth: "auto" },
      4: { halign: "center", cellWidth: 18 },
      5: { halign: "center", cellWidth: 18 },
    },
  });

  // Legend below the table
  let y = doc.lastAutoTable.finalY + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(85);
  doc.text(
    `Place codes: 1=1st  2=2nd  3=3rd  HC=Highly Commended  C=Commended`,
    SIDE_MARGIN_MM,
    y
  );

  drawFooter(doc);
  doc.save(safeFile(competition.competition_name, "scoresheet"));
}

const PLACEMENT_LABEL = {
  FIRST: "1st",
  SECOND: "2nd",
  THIRD: "3rd",
  HIGHLY_COMMENDED: "Highly Commended",
  COMMENDED: "Commended",
  NONE: "",
};

// Compact codes used in the Results PDF Place column so the cell width
// matches the Scoresheet's 18mm. Full names sit in the legend instead.
const PLACEMENT_SHORT = {
  FIRST: "1",
  SECOND: "2",
  THIRD: "3",
  HIGHLY_COMMENDED: "HC",
  COMMENDED: "C",
  NONE: "",
};

const PLACEMENT_BG = {
  FIRST: [254, 243, 199],
  SECOND: [243, 244, 246],
  THIRD: [255, 237, 213],
  HIGHLY_COMMENDED: [236, 254, 255],
  COMMENDED: [245, 243, 255],
  NONE: null,
};

/**
 * Results report PDF — ranked entries with placement tinted cells.
 */
export function savePdfA4Portrait_Results({ competition, season, entries, clubName }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const startY = drawHeader(doc, { competition, season, subtitle: "Results Report", clubName });

  // Summary line is intentionally omitted so the table starts at the
  // same Y position as the Scoresheet — the entry count + highest score
  // are visible from the table itself, and the score range now appears
  // in the legend below.
  autoTable(doc, {
    startY,
    head: [["#", "I.D.", "Photographer", "Image Title", "Score", "Place"]],
    body: entries.map((e, i) => [
      String(i + 1),
      e.photographer_code || "",
      e.photographer_name || "",
      e.image_title || "",
      e.score != null ? String(e.score) : "",
      PLACEMENT_SHORT[e.placement] || "",
    ]),
    margin: { left: SIDE_MARGIN_MM, right: SIDE_MARGIN_MM, top: 14, bottom: 12 },
    theme: "grid",
    headStyles: {
      fillColor: [243, 243, 243],
      textColor: [34, 34, 34],
      fontStyle: "bold",
      fontSize: 7.5,
      lineColor: [153, 153, 153],
      lineWidth: 0.2,
      halign: "left",
      cellPadding: 1.5,
      minCellHeight: 5.5,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 1.2,
      textColor: [17, 17, 17],
      lineColor: [153, 153, 153],
      lineWidth: 0.2,
      minCellHeight: 5.5,
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: {
      0: { halign: "center", cellWidth: 9, textColor: [102, 102, 102] },
      1: { fontStyle: "bold", cellWidth: 20 },
      2: { cellWidth: 40 },
      3: { cellWidth: "auto" },
      4: { halign: "center", cellWidth: 18, fontStyle: "bold" },
      5: { halign: "center", cellWidth: 18 },
    },
    didParseCell: (data) => {
      if (data.section !== "body" || data.column.index !== 5) return;
      const placement = entries[data.row.index]?.placement;
      const tint = PLACEMENT_BG[placement];
      if (tint) {
        data.cell.styles.fillColor = tint;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  // Legend below the table — same format as the scoresheet so the two
  // PDFs sit alongside each other consistently.
  let legendY = doc.lastAutoTable.finalY + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(85);
  doc.text(
    `Place codes: 1=1st  2=2nd  3=3rd  HC=Highly Commended  C=Commended`,
    SIDE_MARGIN_MM,
    legendY
  );

  drawFooter(doc);
  doc.save(safeFile(competition.competition_name, "results"));
}
